import * as webStorage from '@com.rakuten.esd/myr-web-storage';
import { TrackJS } from 'trackjs';
import { AddressSearchAction } from './addressSearchAPIActions.constant';
import {
    ErrorResolution,
    ErrorResolutionTypes,
} from '../../app/common/errorResolution/errorResolution';
import { HttpStatus } from '../httpStatus.constant';
import { IAddressSearchResponseBody, IMasterAddress } from './addressSearch';
import { AddressesCache } from '../../details/addresses/addressesCache';
import store from '../../utils/store';

interface IAddressSearchData {
    error: null | ErrorResolution;
    address: null | IMasterAddress;
}
class AddressSearchAPI {
    public async searchAddress(postalCode: string): Promise<IAddressSearchData> {
        const searchAddressCache = AddressesCache.get<IMasterAddress>(postalCode, 'searchAddress');
        if (searchAddressCache !== null) {
            return {
                error: null,
                address: searchAddressCache,
            };
        }
        try {
            const res = await this.sendRequest<IAddressSearchResponseBody>(
                AddressSearchAction.ADDRESS,
                postalCode,
            );
            if (res && res.address) {
                AddressesCache.store<IMasterAddress>(res.postal_code, res.address);
                return {
                    error: null,
                    address: res.address,
                };
            } else {
                return {
                    error: null,
                    address: null,
                };
            }
        } catch (error) {
            return {
                error: error,
                address: null,
            };
        }
    }

    public sendRequest<T>(action: AddressSearchAction, postalCode: string): Promise<T> {
        return new Promise(async (resolve, reject) => {
            const abortController = new AbortController();
            const requestInit = this.getRequestInit(abortController.signal);

            const endpointUrl = this.getEndpointUrl(action, postalCode);

            if (!requestInit || !endpointUrl) {
                return reject({ type: ErrorResolutionTypes.GENERIC_MESSAGE } as ErrorResolution);
            }

            try {
                const fetchResponse = await fetch(endpointUrl, requestInit);
                const { ok } = fetchResponse;
                if (ok) {
                    const response = await fetchResponse.json();
                    resolve(response);
                    return;
                } else {
                    const errorResolution = await this.resolveOtherErrorResponses(
                        endpointUrl,
                        fetchResponse,
                    );
                    return reject(errorResolution);
                }
            } catch (error) {
                if (error) {
                    TrackJS.track(error);
                    return reject({
                        type: ErrorResolutionTypes.GENERIC_MESSAGE,
                    } as ErrorResolution);
                }
            }
        });
    }

    private getRequestInit(signal?: AbortSignal): RequestInit {
        const requestInit = {
            mode: 'cors',
        } as RequestInit;

        requestInit.headers = this.getRequestHeaders();

        if (signal) {
            requestInit.signal = signal;
        }

        return requestInit;
    }

    private getRequestHeaders(): HeadersInit {
        const headers = new Headers();

        headers.append('Authorization', `Bearer ${webStorage.getAccessToken()}`);

        return headers;
    }

    private getEndpointUrl(action: AddressSearchAction, postal_code: string): string {
        const ADDRESS_SEARCH_URL = store.getState().app.appConfig.BASE_API_URL;
        const endpointUrl = new URL(`${ADDRESS_SEARCH_URL}address/search/${postal_code}`);

        return endpointUrl.toString();
    }

    private async resolveOtherErrorResponses(
        endpoint: string,
        fetchResponse: Response,
    ): Promise<ErrorResolution> {
        const { status } = fetchResponse;
        TrackJS.track(`MyR addressSearchAPI call to ${endpoint} returned status ${status}`);

        switch (status) {
            case HttpStatus.BAD_REQUEST:
                return {
                    errorMessageId: 'notifications.try_different_page',
                    type: ErrorResolutionTypes.GENERIC_MESSAGE,
                };
            case HttpStatus.NOT_FOUND:
                return {
                    errorMessageId: 'notifications.address_search.not_found',
                    type: ErrorResolutionTypes.GENERIC_MESSAGE,
                };
            case HttpStatus.INTERNAL_SERVER_ERROR:
            case HttpStatus.BAD_GATEWAY:
            case HttpStatus.SERVICE_UNAVAILABLE:
            case HttpStatus.GATEWAY_TIMEOUT:
            default:
                return {
                    errorMessageId: 'notifications.try_different_page',
                    type: ErrorResolutionTypes.GENERIC_MESSAGE,
                };
        }
    }
}

export const AddressSearchService = new AddressSearchAPI();
